import { createContext, useCallback, useContext, useState } from 'react';

export const ASC = 'asc';
export const DESC = 'desc';

type SortValueFunction = (record: Record<string, any>) => string | number;

export function useSortableRecordState(
  records: Record<string, any>[],
  valueMap: (Record<string, SortValueFunction>) = {}
) {
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<string>(ASC);
  const [sortedRecords, setSortedRecords] = useState<Record<string, any>[]>(records);

  const sortValueFor = useCallback((record: Record<string, any>, attribute: string) => {
    if (valueMap[attribute]) {
      return valueMap[attribute](record);
    }
    return record[attribute];
  }, [valueMap]);

  const sortRecords = (records: Record<string, any>[], sortCol: string | null, sortDir: string) => {
    if (sortCol) {
      return records.sort((a, b) => {
        const aValue = sortValueFor(a, sortCol) || '';
        const bValue = sortValueFor(b, sortCol) || '';

        if (aValue < bValue) {
          return sortDir === ASC ? -1 : 1;
        } else if (aValue > bValue) {
          return sortDir === ASC ? 1 : -1;
        } else {
          return 0;
        }
      });
    }
    return records;
  };

  const toggleSort = (column: string) => {
    let newSortDir: string = ASC;
    if (sortCol === column) {
      newSortDir = sortDir === ASC ? DESC : ASC;
    }
    const newSortedRecords = sortRecords(sortedRecords, column, newSortDir);
    setSortCol(column);
    setSortDir(newSortDir);
    setSortedRecords(newSortedRecords);
  };

  const addRecord = (record: Record<string, any>) => {
    const newRecords = [...sortedRecords, record];
    const newSortedRecords = sortRecords(newRecords, sortCol, sortDir);
    setSortedRecords(newSortedRecords);
  };

  return {
    sortCol,
    sortDir,
    sortedRecords,
    addRecord,
    toggleSort,
  };
}

interface SortableRecordContextType {
  sortCol?: string | null;
  sortDir?: string;
  toggleSort?: (column: string) => void;
}

export const SortableRecordContext = createContext<SortableRecordContextType>({});

export function useSortableRecordContext() {
  return useContext(SortableRecordContext);
}
